import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { API } from "../App";
import Footer from "../components/Footer";
import PasswordStrengthIndicator from "../components/PasswordStrengthIndicator";

export default function Register({ auth }) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);

  // Check if password meets all requirements
  const isPasswordValid = () => {
    return password.length >= 8 &&
           /[A-Z]/.test(password) &&
           /[a-z]/.test(password) &&
           /\d/.test(password) &&
           /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'`~]/.test(password);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!agreedToTerms) {
      toast.error("Please agree to the Terms & Care to continue");
      return;
    }
    
    if (!isPasswordValid()) {
      toast.error("Please create a stronger password");
      return;
    }
    
    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name, email, password })
      });

      // Read response text first to avoid body stream issues
      const responseText = await response.text();
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Parse error:', parseError);
        throw new Error('Something went wrong. Please try again.');
      }
      
      if (!response.ok) {
        if (data.detail?.error === 'password_invalid') {
          toast.error(data.detail.errors?.[0] || 'Password does not meet requirements');
        } else {
          throw new Error(data.detail || 'Registration failed');
        }
        return;
      }

      auth.login(data);
      toast.success("Welcome! Please check your email to verify your account.");
      navigate('/subscription');
    } catch (error) {
      // Provide a friendlier error message for network/body issues
      if (error.message?.includes('Body') || error.message?.includes('locked') || error.message?.includes('disturbed')) {
        toast.error("Connection interrupted. Please try again.");
      } else {
        toast.error(error.message || "Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md animate-fade-in">
        {/* Logo */}
        <Link to="/" className="block text-center mb-12">
          <h1 
            className="text-3xl font-light text-[#2D2A26]"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            the soft ritual
          </h1>
        </Link>

        {/* Card */}
        <div className="paper-card rounded-3xl p-8 md:p-10">
          <h2 
            className="text-2xl font-normal text-[#2D2A26] text-center mb-2"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Create your space
          </h2>
          <p className="text-[#6E6A66] text-center font-light mb-8">
            Begin with a breath
          </p>

          {/* Email Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-[#6E6A66] font-light">Your name</Label>
              <Input
                id="name"
                type="text"
                data-testid="name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="What should we call you?"
                required
                className="rounded-xl py-6 border-[#E6E2DD] focus:border-[#A87C6D] focus:ring-[#A87C6D] bg-white"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#6E6A66] font-light">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="rounded-xl py-6 border-[#E6E2DD] focus:border-[#A87C6D] focus:ring-[#A87C6D] bg-white"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#6E6A66] font-light">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  data-testid="password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a strong password"
                  required
                  className="rounded-xl py-6 pr-12 border-[#E6E2DD] focus:border-[#A87C6D] focus:ring-[#A87C6D] bg-white"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A8A4A0] hover:text-[#6E6A66] transition-colors"
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </button>
              </div>
              <PasswordStrengthIndicator password={password} />
            </div>

            {/* Terms & Care Checkbox */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="terms"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-[#E6E2DD] text-[#A87C6D] focus:ring-[#A87C6D] cursor-pointer"
              />
              <label htmlFor="terms" className="text-sm text-[#6E6A66] font-light cursor-pointer">
                I agree to the{" "}
                <Link to="/terms" target="_blank" className="text-[#A87C6D] hover:underline">
                  Terms & Care
                </Link>
              </label>
            </div>

            <Button
              type="submit"
              data-testid="register-submit-btn"
              disabled={loading || !isPasswordValid() || !agreedToTerms}
              className="w-full rounded-full py-6 bg-[#A87C6D] hover:bg-[#956d5f] text-white text-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Creating your space..." : "Create account"}
            </Button>
          </form>

          <p className="text-center text-[#6E6A66] font-light mt-6 text-sm">
            This is not therapy or medical advice.
          </p>

          <p className="text-center text-[#6E6A66] font-light mt-4">
            Already have an account?{" "}
            <Link to="/login" className="text-[#A87C6D] hover:underline">
              Sign in
            </Link>
          </p>
        </div>

        {/* Back link */}
        <p className="text-center mt-8">
          <Link to="/" className="text-[#A8A4A0] hover:text-[#6E6A66] text-sm">
            ← Back to home
          </Link>
        </p>
        </div>
      </div>
      <Footer />
    </div>
  );
}
